import { Component, computed, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconComponent } from '@cca-ui/icon';
import { NAVIGATOR } from '@cca-environment';
import { environment } from '../../environments/environment';
import { DEFAULT_ENDPOINT_ID } from '../data/portal-resources';
import { type ApiEndpoint, type HttpMethod } from '../data/portal-types';
import {
  buildCodeSample,
  buildResponseSample,
  DEFAULT_SAMPLE_LANGUAGE,
  SAMPLE_LANGUAGES,
  type SampleLanguage,
} from '../data/code-samples';
import { ApiSpecService } from '../services/api-spec.service';
import { PortalSearchService } from '../search/portal-search.service';

interface ApiNavGroup {
  readonly name: string;
  readonly endpoints: readonly ApiEndpoint[];
}

type CopyTarget = 'request' | 'response';

const METHOD_CHIP_CLASSES: Record<HttpMethod, string> = {
  GET: 'badge-text-blue badge-bg-blue-light',
  POST: 'badge-text-green badge-bg-green-light',
  PATCH: 'badge-text-orange badge-bg-orange-light',
  DELETE: 'badge-text-red badge-bg-red-light',
};

/**
 * API reference view: endpoint navigation, endpoint detail, and language-tabbed code samples.
 */
@Component({
  selector: 'cca-api-reference-page',
  templateUrl: './api-reference-page.component.html',
  imports: [RouterLink, IconComponent],
})
export class ApiReferencePageComponent {
  private readonly apiSpec = inject(ApiSpecService);
  private readonly search = inject(PortalSearchService);
  private readonly navigator = inject(NAVIGATOR);

  readonly endpointId = input.required<string>();

  protected readonly languages = SAMPLE_LANGUAGES;
  protected readonly openApiSpecUrl = environment.swaggerUrl;
  protected readonly selectedLanguage = signal<SampleLanguage>(DEFAULT_SAMPLE_LANGUAGE);
  protected readonly copiedTarget = signal<CopyTarget | null>(null);

  protected readonly endpoint = computed(() => {
    const endpoints = this.apiSpec.endpoints();
    return (
      endpoints.find((candidate) => candidate.id === this.endpointId()) ??
      endpoints.find((candidate) => candidate.id === DEFAULT_ENDPOINT_ID) ??
      endpoints[0]
    );
  });

  protected readonly navGroups = computed<readonly ApiNavGroup[]>(() => {
    const endpoints = this.apiSpec.endpoints();
    const query = this.search.normalizedQuery();
    return this.apiSpec
      .groupNames()
      .map((name) => ({
        name,
        endpoints: endpoints.filter(
          (candidate) =>
            candidate.group === name &&
            (!query || `${candidate.title} ${candidate.path}`.toLowerCase().includes(query)),
        ),
      }))
      .filter((group) => group.endpoints.length > 0);
  });

  protected readonly requestSample = computed(() =>
    buildCodeSample(this.endpoint(), this.selectedLanguage()),
  );

  protected readonly responseSample = computed(() => buildResponseSample(this.endpoint()));

  protected readonly responseStatus = computed(() => this.endpoint().responseStatus ?? '200 OK');

  protected methodChipClass(method: HttpMethod): string {
    return METHOD_CHIP_CLASSES[method];
  }

  protected selectLanguage(language: SampleLanguage): void {
    this.selectedLanguage.set(language);
  }

  protected async copySample(target: CopyTarget): Promise<void> {
    const sample = target === 'request' ? this.requestSample() : this.responseSample();
    if (!sample) {
      return;
    }
    try {
      await this.navigator?.clipboard.writeText(sample);
      this.copiedTarget.set(target);
      setTimeout(() => this.copiedTarget.set(null), 1500);
    } catch {
      // Clipboard access can be denied; copying is a convenience, so fail silently.
    }
  }
}
